/**
 * Compact **positional binary** codec for the hot per-tick world messages
 * (ADR-0190). The three world payloads - {@link WorldSnapshotMessage},
 * {@link WorldDeltaMessage}, {@link WorldKeyframeMessage} - carry on the order of
 * ~200 entities per tick, and `JSON.stringify`-ing that entity set was profiled as
 * the dominant server-tick CPU cost. This module encodes those messages field-by-
 * field in binary on the **server** and decodes them on the **client**, cutting both
 * that stringify CPU and the bytes on the wire (positional encoding drops every JSON
 * key name, brace, quote, and comma).
 *
 * Scope is deliberately narrow (see ADR-0190):
 *
 * - **Only the three world message kinds** ride this codec; every other, low-frequency
 *   message (chat, gold, storm status, duels, admin, …) and every client->server
 *   message stay plain JSON. {@link import('./protocol').encode}/`decode` dispatch on
 *   `kind` so the rest of the system is untouched.
 * - **Server-encode + client-decode only.** Only the server produces these messages,
 *   so there is no client-encode/server-decode direction to maintain.
 * - The **entity component set** is encoded positionally (the hot bytes); two cold
 *   sub-parts - the static `map` (rides only a Snapshot, sent on connect) and the
 *   pending `commandQueue` (almost always empty) - are embedded as length-prefixed
 *   JSON blobs. They are not the CPU cost and this keeps their fidelity trivially
 *   exact for the full `Command`/`MapData` shapes with zero schema surface.
 *
 * **Fidelity is exact.** A decoded world is byte-identical to what the JSON path
 * produced (the round-trip tests pin `JSON.stringify(serialize(...))` equality). Every
 * JS number rides a self-describing exact codec ({@link BinaryWriter.number}); a JS
 * number *is* an IEEE-754 float64, so the float fallback is lossless, and small
 * non-negative integers (the overwhelming common case: tiles, ids, hues, facings)
 * compress to a LEB128 varint.
 *
 * **Unknown components never drop.** The known component set is encoded by a bitmask;
 * any component key the codec does not recognise (a concurrently-added marker) is
 * carried in an `extras` section as `name + JSON` so it round-trips losslessly rather
 * than being silently dropped - the same generic-over-the-component-set property
 * {@link import('./protocol').diffEntity} has (ADR-0164).
 *
 * **Framed and versioned.** Every binary frame opens with a magic byte + format
 * version byte so a mismatched frame fails loudly (throws) rather than corrupting
 * silently, and so the codec can evolve.
 */

import type { EntitySnapshot } from '../sim/persistence';
import type { MobileState, TilePoint } from '../sim/mobile';
import type { EntityId } from '../sim/entity';
import type {
  BanishComponent,
  ChestComponent,
  DeathComponent,
  DiceComponent,
  DoorComponent,
  EquippedItem,
  HealthComponent,
  MoongateComponent,
  PickupComponent,
  InteractionComponent,
  InteractionRole,
  PositionComponent,
  ProjectileComponent,
  RenderableComponent,
  TeleporterComponent,
  XpGemComponent,
} from '../sim/entity';
import type { EquipLayer, ProjectileEffectId } from '../sim/content';
import {
  CODEC_KNOWN_COMPONENTS,
  type CodecKnownComponent,
} from '../sim/componentDescriptors';
import type {
  WorldSnapshotMessage,
  WorldDeltaMessage,
  WorldKeyframeMessage,
  EntityFieldDelta,
  EntityComponentKey,
} from './protocol';

/** The three message kinds this binary codec owns; everything else stays JSON. */
export type WorldMessage = WorldSnapshotMessage | WorldDeltaMessage | WorldKeyframeMessage;

/**
 * First byte of every binary frame. Chosen so it can never begin a JSON text frame
 * (`{`/`[`/whitespace/digit), but the receive side actually distinguishes binary from
 * text by the frame's data type (ArrayBuffer/Buffer vs string), so this is a
 * belt-and-suspenders integrity tag read by {@link decodeWorldMessage}.
 */
const MAGIC = 0xb1;

/**
 * Binary format version. Bumped when the positional layout changes in a way that
 * makes an older reader wrong. A frame carrying any other version throws in
 * {@link decodeWorldMessage} (the client shows it as a dropped frame, never garbage).
 */
const FORMAT_VERSION = 1;

/** Frame-kind discriminators (byte 3 of a frame). */
const KIND_SNAPSHOT = 1;
const KIND_DELTA = 2;
const KIND_KEYFRAME = 3;

/** Number-codec tags (see {@link BinaryWriter.number}). */
const NUM_UINT = 0;
const NUM_NINT = 1;
const NUM_FLOAT = 2;

/**
 * The component keys of an {@link EntitySnapshot} the codec knows how to encode
 * positionally, in a **fixed order** (the bitmask bit index is this array index, so
 * the order is load-bearing - append new components, never reorder). Any component
 * outside this set rides the `extras` JSON escape hatch instead (see the module
 * doc). `id` is not here: it is written before the component bag.
 *
 * Since chassis Slice A (ADR-0260) the list is **declared in the component
 * descriptor table** (`src/sim/componentDescriptors.ts`, beside each component's
 * persistence shape) and only *consumed* here; the
 * {@link writeComponent}/{@link readComponent} switches below are made
 * compile-exhaustive against that declaration by their
 * `default: assertComponentCase(key)` never-guards (load-bearing: without them a
 * missing case passes `tsc` - `writeComponent` returns void and the tsconfig has
 * no `noImplicitReturns`), so appending a codec key without its read/write cases
 * fails `tsc --noEmit` at both switches naming the key.
 */
const KNOWN_COMPONENTS = CODEC_KNOWN_COMPONENTS;

type KnownComponent = CodecKnownComponent;

const KNOWN_SET: ReadonlySet<string> = new Set(KNOWN_COMPONENTS);

/** Sentinel byte for a `null` {@link MobileState.intent} (real directions are 0-7). */
const INTENT_NULL = 0xff;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

/**
 * A growable little-endian binary sink. Not a general-purpose buffer: it exposes just
 * the primitives the world codec needs (varint, self-describing number, string, bool,
 * raw bytes), sized to grow amortized-O(1). {@link finish} returns the written bytes.
 */
class BinaryWriter {
  private buf = new Uint8Array(1024);
  private len = 0;
  private readonly floatView = new DataView(new ArrayBuffer(8));

  private ensure(extra: number): void {
    const need = this.len + extra;
    if (need <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < need) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf.subarray(0, this.len));
    this.buf = next;
  }

  u8(b: number): void {
    if (this.len >= this.buf.length) this.ensure(1);
    this.buf[this.len++] = b & 0xff;
  }

  bool(v: boolean): void {
    if (this.len >= this.buf.length) this.ensure(1);
    this.buf[this.len++] = v ? 1 : 0;
  }

  /** Unsigned LEB128 over a non-negative safe integer (exact to 2^53). */
  varuint(n: number): void {
    this.ensure(8); // a 2^53 varint is at most 8 bytes; write without per-byte checks
    let len = this.len;
    const buf = this.buf;
    // `% 128` / `Math.floor(/128)` (not bitwise) so values above 2^31 stay exact.
    while (n > 0x7f) {
      buf[len++] = (n % 128) + 0x80;
      n = Math.floor(n / 128);
    }
    buf[len++] = n;
    this.len = len;
  }

  /**
   * A self-describing, exact codec for **any** JS number: a 1-byte tag then either a
   * varint (safe integers, the compact common case) or a raw float64 (everything
   * else, lossless because a JS number is a float64). `-0` normalizes to `0`, exactly
   * as `JSON.stringify` does, so the binary and JSON paths agree.
   *
   * The non-negative safe-integer path (tiles, ids, hues, facings, tick scalars - the
   * overwhelming common case) is inlined into one `ensure(9)` + a tight write loop, so
   * the hot per-entity fields cost no per-byte bounds checks or nested method calls.
   */
  number(n: number): void {
    if (Number.isSafeInteger(n)) {
      if (n >= 0) {
        // Fast path: tag byte + LEB128, no bounds check per byte (max 1 + 8 bytes).
        this.ensure(9);
        let len = this.len;
        const buf = this.buf;
        buf[len++] = NUM_UINT;
        while (n > 0x7f) {
          buf[len++] = (n % 128) + 0x80;
          n = Math.floor(n / 128);
        }
        buf[len++] = n;
        this.len = len;
      } else {
        this.u8(NUM_NINT);
        this.varuint(-n);
      }
    } else {
      this.u8(NUM_FLOAT);
      this.ensure(8);
      this.floatView.setFloat64(0, n, true);
      let len = this.len;
      const buf = this.buf;
      const fv = this.floatView;
      for (let i = 0; i < 8; i++) buf[len++] = fv.getUint8(i);
      this.len = len;
    }
  }

  /**
   * A length-prefixed UTF-8 string. Names/ids/layers are almost always pure ASCII, so
   * a fast path writes their bytes directly via `charCodeAt` - `TextEncoder.encode`
   * has heavy per-call overhead (profiled as the dominant keyframe-encode cost with
   * ~200 names) and is used only when a non-ASCII code unit is present.
   */
  string(s: string): void {
    const n = s.length;
    let ascii = true;
    for (let i = 0; i < n; i++) {
      if (s.charCodeAt(i) > 0x7f) {
        ascii = false;
        break;
      }
    }
    if (ascii) {
      // ASCII: one byte per char, so byte length == string length.
      this.varuint(n);
      this.ensure(n);
      let len = this.len;
      const buf = this.buf;
      for (let i = 0; i < n; i++) buf[len++] = s.charCodeAt(i);
      this.len = len;
      return;
    }
    const bytes = textEncoder.encode(s);
    this.varuint(bytes.length);
    this.ensure(bytes.length);
    this.buf.set(bytes, this.len);
    this.len += bytes.length;
  }

  /**
   * Append an already-encoded byte run verbatim - one `set` memcpy, no per-byte
   * loop and no re-encoding. The escape hatch behind {@link writeMapSection}'s
   * memo (PRD #825): the caller owns producing bytes that are byte-identical to
   * what the equivalent typed writes would have emitted.
   */
  raw(bytes: Uint8Array): void {
    this.ensure(bytes.length);
    this.buf.set(bytes, this.len);
    this.len += bytes.length;
  }

  finish(): Uint8Array {
    return this.buf.slice(0, this.len);
  }
}

/** The read counterpart of {@link BinaryWriter}; advances a cursor over the frame. */
class BinaryReader {
  private pos = 0;
  private readonly floatView = new DataView(new ArrayBuffer(8));

  constructor(private readonly bytes: Uint8Array) {}

  u8(): number {
    if (this.pos >= this.bytes.length) throw new Error('binary frame underflow');
    return this.bytes[this.pos++]!;
  }

  bool(): boolean {
    return this.u8() !== 0;
  }

  varuint(): number {
    let result = 0;
    let mult = 1;
    let b: number;
    do {
      b = this.u8();
      result += (b & 0x7f) * mult;
      mult *= 128;
    } while (b & 0x80);
    return result;
  }

  number(): number {
    const tag = this.u8();
    if (tag === NUM_UINT) return this.varuint();
    if (tag === NUM_NINT) return -this.varuint();
    if (tag === NUM_FLOAT) {
      for (let i = 0; i < 8; i++) this.floatView.setUint8(i, this.u8());
      return this.floatView.getFloat64(0, true);
    }
    throw new Error(`bad number tag ${tag}`);
  }

  string(): string {
    const len = this.varuint();
    if (this.pos + len > this.bytes.length) throw new Error('binary frame underflow');
    const bytes = this.bytes;
    const start = this.pos;
    const end = start + len;
    // Fast ASCII path (the mirror of the writer's): decode byte-per-char without
    // TextDecoder's per-call overhead when no byte has the high bit set.
    let ascii = true;
    for (let i = start; i < end; i++) {
      if (bytes[i]! > 0x7f) {
        ascii = false;
        break;
      }
    }
    this.pos = end;
    if (ascii) {
      let s = '';
      for (let i = start; i < end; i++) s += String.fromCharCode(bytes[i]!);
      return s;
    }
    return textDecoder.decode(bytes.subarray(start, end));
  }
}

// --- Per-component codecs -------------------------------------------------------

function writePosition(w: BinaryWriter, p: PositionComponent): void {
  const hasZ = p.z !== undefined;
  w.u8(hasZ ? 1 : 0);
  w.number(p.x);
  w.number(p.y);
  w.number(p.facing);
  if (hasZ) w.number(p.z!);
}

function readPosition(r: BinaryReader): PositionComponent {
  const flags = r.u8();
  const x = r.number();
  const y = r.number();
  const facing = r.number();
  const p: PositionComponent = { x, y, facing };
  if (flags & 1) p.z = r.number();
  return p;
}

function writeEquipment(w: BinaryWriter, items: EquippedItem[]): void {
  w.varuint(items.length);
  for (const item of items) {
    const hasHue = item.hue !== undefined;
    w.u8(hasHue ? 1 : 0);
    w.string(item.id);
    w.string(item.layer);
    if (hasHue) w.number(item.hue!);
  }
}

function readEquipment(r: BinaryReader): EquippedItem[] {
  const n = r.varuint();
  const items: EquippedItem[] = [];
  for (let i = 0; i < n; i++) {
    const flags = r.u8();
    const id = r.string();
    const layer = r.string() as EquipLayer;
    const item: EquippedItem = { id, layer };
    if (flags & 1) item.hue = r.number();
    items.push(item);
  }
  return items;
}

function writeRenderable(w: BinaryWriter, rd: RenderableComponent): void {
  let flags = 0;
  if (rd.bodyHue !== undefined) flags |= 1;
  if (rd.body !== undefined) flags |= 2;
  if (rd.name !== undefined) flags |= 4;
  if (rd.equipment !== undefined) flags |= 8;
  if (rd.combatStance !== undefined) flags |= 16;
  if (rd.title !== undefined) flags |= 32;
  if (rd.scale !== undefined) flags |= 64;
  // NOTE: 128 is the LAST free bit in this u8 presence mask. The next optional
  // renderable field must widen the mask (e.g. a u16, or an escape bit) rather than
  // silently overflowing - a 9th flag would wrap to 0 and drop the field on the wire.
  if (rd.mount !== undefined) flags |= 128;
  w.u8(flags);
  if (flags & 1) w.number(rd.bodyHue!);
  if (flags & 2) w.number(rd.body!);
  if (flags & 4) w.string(rd.name!);
  if (flags & 8) writeEquipment(w, rd.equipment!);
  if (flags & 16) w.bool(rd.combatStance!);
  if (flags & 32) w.string(rd.title!);
  if (flags & 64) w.number(rd.scale!);
  if (flags & 128) w.number(rd.mount!);
}

function readRenderable(r: BinaryReader): RenderableComponent {
  const flags = r.u8();
  const rd: RenderableComponent = {};
  if (flags & 1) rd.bodyHue = r.number();
  if (flags & 2) rd.body = r.number();
  if (flags & 4) rd.name = r.string();
  if (flags & 8) rd.equipment = readEquipment(r);
  if (flags & 16) rd.combatStance = r.bool();
  if (flags & 32) rd.title = r.string();
  if (flags & 64) rd.scale = r.number();
  if (flags & 128) rd.mount = r.number();
  return rd;
}

/**
 * The {@link MobileState} - the biggest per-entity payload and the delta hot path
 * (a walking mobile re-sends its pose every tick). Every field {@link Mobile.toState}
 * emits is written **unconditionally** in a fixed order: `toState` always populates
 * all of them (including `path`, `moonwalkRemaining`, `stepIsMoonwalk`), so there is
 * no presence flag to spend - the layout is fully positional.
 */
function writeMobile(w: BinaryWriter, m: MobileState): void {
  w.number(m.x);
  w.number(m.y);
  w.number(m.dir);
  w.number(m.msPerTile);
  w.number(m.msPerTileRun);
  w.number(m.turnDelayMs);
  w.number(m.turnDelayFastMs);
  w.bool(m.fastRotation);
  w.bool(m.moving);
  w.number(m.fromX);
  w.number(m.fromY);
  w.number(m.toX);
  w.number(m.toY);
  w.number(m.elapsed);
  w.number(m.stepMs);
  w.bool(m.stepRunning);
  w.bool(m.turning);
  w.number(m.turnElapsed);
  // intent is Direction | null; 0xFF marks null (real directions are 0-7).
  w.u8(m.intent === null || m.intent === undefined ? INTENT_NULL : m.intent);
  w.bool(m.intentRunning);
  w.bool(m.intentTurnOnly);
  const path = m.path ?? [];
  w.varuint(path.length);
  for (const pt of path) {
    w.number(pt.x);
    w.number(pt.y);
  }
  w.number(m.moonwalkRemaining ?? 0);
  w.bool(m.stepIsMoonwalk ?? false);
}

function readMobile(r: BinaryReader): MobileState {
  const x = r.number();
  const y = r.number();
  const dir = r.number();
  const msPerTile = r.number();
  const msPerTileRun = r.number();
  const turnDelayMs = r.number();
  const turnDelayFastMs = r.number();
  const fastRotation = r.bool();
  const moving = r.bool();
  const fromX = r.number();
  const fromY = r.number();
  const toX = r.number();
  const toY = r.number();
  const elapsed = r.number();
  const stepMs = r.number();
  const stepRunning = r.bool();
  const turning = r.bool();
  const turnElapsed = r.number();
  const intentByte = r.u8();
  const intent = intentByte === INTENT_NULL ? null : intentByte;
  const intentRunning = r.bool();
  const intentTurnOnly = r.bool();
  const pathLen = r.varuint();
  const path: TilePoint[] = [];
  for (let i = 0; i < pathLen; i++) path.push({ x: r.number(), y: r.number() });
  const moonwalkRemaining = r.number();
  const stepIsMoonwalk = r.bool();
  return {
    x,
    y,
    dir,
    msPerTile,
    msPerTileRun,
    turnDelayMs,
    turnDelayFastMs,
    fastRotation,
    moving,
    fromX,
    fromY,
    toX,
    toY,
    elapsed,
    stepMs,
    stepRunning,
    turning,
    turnElapsed,
    intent,
    intentRunning,
    intentTurnOnly,
    path,
    moonwalkRemaining,
    stepIsMoonwalk,
  };
}

function writeDoor(w: BinaryWriter, d: DoorComponent): void {
  w.number(d.base);
  w.number(d.facing);
  w.bool(d.open);
}

function readDoor(r: BinaryReader): DoorComponent {
  const base = r.number();
  const facing = r.number();
  const open = r.bool();
  return { base, facing, open };
}

function writeDice(w: BinaryWriter, d: DiceComponent): void {
  w.number(d.artId);
}

function readDice(r: BinaryReader): DiceComponent {
  return { artId: r.number() };
}

function writeTeleporter(w: BinaryWriter, t: TeleporterComponent): void {
  w.number(t.to.x);
  w.number(t.to.y);
  w.number(t.to.z);
}

function readTeleporter(r: BinaryReader): TeleporterComponent {
  const x = r.number();
  const y = r.number();
  const z = r.number();
  return { to: { x, y, z } };
}

function writeHealth(w: BinaryWriter, h: HealthComponent): void {
  const hasPaused = h.regenPaused !== undefined;
  w.u8(hasPaused ? 1 : 0);
  w.number(h.current);
  w.number(h.max);
  if (hasPaused) w.bool(h.regenPaused!);
}

function readHealth(r: BinaryReader): HealthComponent {
  const flags = r.u8();
  const current = r.number();
  const max = r.number();
  const h: HealthComponent = { current, max };
  if (flags & 1) h.regenPaused = r.bool();
  return h;
}

function writeMoongate(w: BinaryWriter, m: MoongateComponent): void {
  // Append-only flag byte: bit 0 is the fixed destination, bit 1 the dungeon purpose.
  const hasTo = m.to !== undefined;
  w.u8((hasTo ? 1 : 0) | (m.purpose === 'dungeon' ? 2 : 0));
  if (hasTo) {
    w.number(m.to!.x);
    w.number(m.to!.y);
    w.number(m.to!.z);
  }
}

function readMoongate(r: BinaryReader): MoongateComponent {
  const flags = r.u8();
  if ((flags & 3) === 3) throw new Error('Invalid moongate flags');
  if (flags & 2) return { purpose: 'dungeon' };
  if (!(flags & 1)) return {};
  const x = r.number();
  const y = r.number();
  const z = r.number();
  return { to: { x, y, z } };
}

function writeChest(w: BinaryWriter, c: ChestComponent): void {
  w.number(c.artId);
}

function writeBanish(w: BinaryWriter, b: BanishComponent): void {
  w.number(b.remainingTicks);
}

function readBanish(r: BinaryReader): BanishComponent {
  return { remainingTicks: r.number() };
}

function readChest(r: BinaryReader): ChestComponent {
  return { artId: r.number() };
}

function writeDeath(w: BinaryWriter, d: DeathComponent): void {
  w.number(d.livingBody);
}

function readDeath(r: BinaryReader): DeathComponent {
  return { livingBody: r.number() };
}

/**
 * The {@link ProjectileComponent} (PRD #399 Slice A). Every field is always present
 * (there is no optional sub-field), so the layout is fully positional with no flag
 * byte - `vx`/`vy` ride the exact float codec (they are fractional per-tick
 * velocities), the rest are small integers, and `look` is a short id string.
 */
function writeProjectile(w: BinaryWriter, p: ProjectileComponent): void {
  w.number(p.vx);
  w.number(p.vy);
  w.number(p.damage);
  w.number(p.pierceLeft);
  w.number(p.ownerId);
  w.number(p.ttlTicks);
  w.string(p.look);
}

function readProjectile(r: BinaryReader): ProjectileComponent {
  const vx = r.number();
  const vy = r.number();
  const damage = r.number();
  const pierceLeft = r.number();
  const ownerId = r.number() as EntityId;
  const ttlTicks = r.number();
  const look = r.string() as ProjectileEffectId;
  return { vx, vy, damage, pierceLeft, ownerId, ttlTicks, look };
}

/** The {@link XpGemComponent} (arena XP orbs): one small integer, the base XP value. */
function writeXpGem(w: BinaryWriter, g: XpGemComponent): void {
  w.number(g.value);
}

function readXpGem(r: BinaryReader): XpGemComponent {
  return { value: r.number() };
}

/** The {@link PickupComponent} (PRD #431 Vacuum): the pickup kind as a short id string. */
function writePickup(w: BinaryWriter, p: PickupComponent): void {
  w.string(p.kind);
}

function readPickup(r: BinaryReader): PickupComponent {
  return { kind: r.string() as PickupComponent['kind'] };
}

/**
 * The {@link InteractionComponent} (PRD #534 D5, ADR-0266): the role as a short id
 * string plus a tri-state flag varint for the optional `raisesCorpse` (0 = absent,
 * 1 = true, 2 = false), so a decoded component deep-equals the original - absent
 * and false are distinct on the wire exactly as they are in the object.
 */
function writeInteraction(w: BinaryWriter, i: InteractionComponent): void {
  w.string(i.role);
  w.number(i.raisesCorpse === undefined ? 0 : i.raisesCorpse ? 1 : 2);
}

function readInteraction(r: BinaryReader): InteractionComponent {
  const role = r.string() as InteractionRole;
  const flag = r.number();
  return { role, ...(flag === 0 ? {} : { raisesCorpse: flag === 1 }) };
}

/**
 * Compile-time exhaustiveness guard for the two per-component switches: in each
 * `default` branch the switched `key` must have narrowed to `never` - if a key is
 * appended to `CODEC_KNOWN_COMPONENTS` without a `writeComponent`/`readComponent`
 * case, `key` narrows to that leftover member instead and this call **fails
 * `tsc --noEmit`** (a CI step) naming it. The throw is unreachable in type-correct
 * code; it exists so a type-cast escape still fails loudly at runtime.
 */
function assertComponentCase(key: never): never {
  throw new Error(
    `no codec case for component '${String(key)}' - add writeComponent/readComponent ` +
      `cases in src/net/binaryCodec.ts`,
  );
}

/** Encode one known component by key (markers write nothing - presence is the bit). */
function writeComponent(w: BinaryWriter, key: KnownComponent, value: unknown): void {
  switch (key) {
    case 'position':
      writePosition(w, value as PositionComponent);
      return;
    case 'renderable':
      writeRenderable(w, value as RenderableComponent);
      return;
    case 'mobile':
      writeMobile(w, value as MobileState);
      return;
    case 'door':
      writeDoor(w, value as DoorComponent);
      return;
    case 'dice':
      writeDice(w, value as DiceComponent);
      return;
    case 'teleporter':
      writeTeleporter(w, value as TeleporterComponent);
      return;
    case 'health':
      writeHealth(w, value as HealthComponent);
      return;
    case 'death':
      writeDeath(w, value as DeathComponent);
      return;
    case 'moongate':
      writeMoongate(w, value as MoongateComponent);
      return;
    case 'chest':
      writeChest(w, value as ChestComponent);
      return;
    case 'banish':
      writeBanish(w, value as BanishComponent);
      return;
    case 'projectile':
      writeProjectile(w, value as ProjectileComponent);
      return;
    case 'xpGem':
      writeXpGem(w, value as XpGemComponent);
      return;
    case 'pickup':
      writePickup(w, value as PickupComponent);
      return;
    case 'interaction':
      writeInteraction(w, value as InteractionComponent);
      return;
    case 'rat':
    case 'vortex':
    case 'horde':
      // Pure markers: the presence bit already says `true`; nothing to write.
      return;
    default:
      assertComponentCase(key);
  }
}

/** Decode one known component by key (markers reconstruct their `true` value). */
function readComponent(r: BinaryReader, key: KnownComponent): unknown {
  switch (key) {
    case 'position':
      return readPosition(r);
    case 'renderable':
      return readRenderable(r);
    case 'mobile':
      return readMobile(r);
    case 'door':
      return readDoor(r);
    case 'dice':
      return readDice(r);
    case 'teleporter':
      return readTeleporter(r);
    case 'health':
      return readHealth(r);
    case 'death':
      return readDeath(r);
    case 'moongate':
      return readMoongate(r);
    case 'chest':
      return readChest(r);
    case 'banish':
      return readBanish(r);
    case 'projectile':
      return readProjectile(r);
    case 'xpGem':
      return readXpGem(r);
    case 'pickup':
      return readPickup(r);
    case 'interaction':
      return readInteraction(r);
    case 'rat':
    case 'vortex':
    case 'horde':
      return true;
    default:
      return assertComponentCase(key);
  }
}

/**
 * A **component bag**: the component fields of an entity (an {@link EntitySnapshot}
 * minus its `id`) or the `set` of an {@link EntityFieldDelta}. Layout: a varint
 * bitmask of which {@link KNOWN_COMPONENTS} are present, each present known component
 * in fixed order, then an `extras` section (`count`, then `name`+`JSON` pairs) for any
 * component key outside the known set. The extras section is what makes an unknown /
 * future component round-trip losslessly instead of dropping (see the module doc).
 */
function writeBag(w: BinaryWriter, obj: Record<string, unknown>): void {
  let mask = 0;
  for (let i = 0; i < KNOWN_COMPONENTS.length; i++) {
    if (obj[KNOWN_COMPONENTS[i]!] !== undefined) mask |= 1 << i;
  }
  w.varuint(mask);
  for (let i = 0; i < KNOWN_COMPONENTS.length; i++) {
    if (mask & (1 << i)) {
      const key = KNOWN_COMPONENTS[i]!;
      writeComponent(w, key, obj[key]);
    }
  }
  const extras = Object.keys(obj).filter((k) => k !== 'id' && !KNOWN_SET.has(k));
  w.varuint(extras.length);
  for (const k of extras) {
    w.string(k);
    w.string(JSON.stringify(obj[k]));
  }
}

function readBag(r: BinaryReader): Record<string, unknown> {
  const mask = r.varuint();
  const obj: Record<string, unknown> = {};
  for (let i = 0; i < KNOWN_COMPONENTS.length; i++) {
    if (mask & (1 << i)) {
      const key = KNOWN_COMPONENTS[i]!;
      obj[key] = readComponent(r, key);
    }
  }
  const extrasCount = r.varuint();
  for (let i = 0; i < extrasCount; i++) {
    const k = r.string();
    obj[k] = JSON.parse(r.string());
  }
  return obj;
}

function writeEntity(w: BinaryWriter, snap: EntitySnapshot): void {
  w.number(snap.id);
  writeBag(w, snap as unknown as Record<string, unknown>);
}

function readEntity(r: BinaryReader): EntitySnapshot {
  const id = r.number() as EntityId;
  const bag = readBag(r);
  return { id, ...bag } as EntitySnapshot;
}

function writeFieldDelta(w: BinaryWriter, delta: EntityFieldDelta): void {
  let flags = 0;
  if (delta.set !== undefined) flags |= 1;
  if (delta.cleared !== undefined) flags |= 2;
  w.u8(flags);
  if (flags & 1) writeBag(w, delta.set as Record<string, unknown>);
  if (flags & 2) {
    w.varuint(delta.cleared!.length);
    for (const key of delta.cleared!) w.string(key);
  }
}

function readFieldDelta(r: BinaryReader): EntityFieldDelta {
  const flags = r.u8();
  const delta: EntityFieldDelta = {};
  if (flags & 1) delta.set = readBag(r) as EntityFieldDelta['set'];
  if (flags & 2) {
    const n = r.varuint();
    const cleared: EntityComponentKey[] = [];
    for (let i = 0; i < n; i++) cleared.push(r.string() as EntityComponentKey);
    delta.cleared = cleared;
  }
  return delta;
}

// --- Message codecs -------------------------------------------------------------

function writeEntities(w: BinaryWriter, entities: Record<EntityId, EntitySnapshot>): void {
  const ids = Object.keys(entities);
  w.varuint(ids.length);
  for (const id of ids) writeEntity(w, entities[id as unknown as EntityId]!);
}

function readEntities(r: BinaryReader): Record<EntityId, EntitySnapshot> {
  const n = r.varuint();
  const entities: Record<EntityId, EntitySnapshot> = {};
  for (let i = 0; i < n; i++) {
    const e = readEntity(r);
    entities[e.id] = e;
  }
  return entities;
}

/**
 * Memoized wire bytes for one static {@link MapData}: the length-prefixed UTF-8
 * JSON blob a `Snapshot` embeds its map as, keyed by the MapData's identity.
 *
 * The map is by far the largest thing that crosses this codec - the town's is
 * ~1.85 MB - and encoding it means a full `JSON.stringify` plus a ~2-million-
 * iteration ASCII scan and copy in {@link BinaryWriter.string}. That was ~13 ms,
 * and it was paid **inside the tick** every time a socket was handed a
 * map-bearing snapshot: on connect, entering an Arena run or dungeon floor, and
 * on every run settlement's return to town (PRD #825).
 *
 * A {@link MapData} is deep-frozen by the {@link GameMap} that owns it and is
 * carried by reference through `serialize`/`applyDelta`/`applyKeyframe`, so its
 * bytes are a pure function of its identity and this memo can never go stale.
 * The `WeakMap` keying means a retired map (a torn-down run world) drops out
 * with its entry.
 */
const mapSectionBytes = new WeakMap<object, Uint8Array>();

/** Write a `Snapshot`'s map blob, reusing {@link mapSectionBytes} when it is warm. */
function writeMapSection(w: BinaryWriter, map: WorldSnapshotMessage['map']): void {
  let bytes = mapSectionBytes.get(map);
  if (bytes === undefined) {
    // Encoded through the very same `string` path, into its own buffer, so the
    // memoized run is byte-for-byte what an inline write would have produced.
    const section = new BinaryWriter();
    section.string(JSON.stringify(map));
    bytes = section.finish();
    mapSectionBytes.set(map, bytes);
  }
  w.raw(bytes);
}

function writeSnapshotBody(w: BinaryWriter, s: WorldSnapshotMessage): void {
  const hasVersion = s.version !== undefined;
  w.u8(hasVersion ? 1 : 0);
  if (hasVersion) w.number(s.version!);
  // The static map rides only a Snapshot (sent on connect), so it is a cold path:
  // embed it as a JSON blob rather than growing a positional MapData schema.
  // Memoized per MapData identity - see writeMapSection.
  writeMapSection(w, s.map);
  w.number(s.rngState);
  w.number(s.idCounter);
  writeEntities(w, s.entities);
  // commandQueue is almost always empty and carries the full `Command` union; embed
  // it as a JSON blob (cold path, trivially exact for any command shape).
  w.string(JSON.stringify(s.commandQueue));
}

function readSnapshotBody(r: BinaryReader): WorldSnapshotMessage {
  const hasVersion = r.u8();
  const version = hasVersion ? r.number() : undefined;
  const map = JSON.parse(r.string());
  const rngState = r.number();
  const idCounter = r.number();
  const entities = readEntities(r);
  const commandQueue = JSON.parse(r.string());
  const msg: WorldSnapshotMessage = {
    kind: 'Snapshot',
    map,
    rngState,
    idCounter,
    entities,
    commandQueue,
  };
  if (version !== undefined) msg.version = version;
  return msg;
}

function writeDeltaBody(w: BinaryWriter, d: WorldDeltaMessage): void {
  w.number(d.rngState);
  w.number(d.idCounter);
  writeEntities(w, d.added);
  const changedIds = Object.keys(d.changed);
  w.varuint(changedIds.length);
  for (const id of changedIds) {
    w.number(Number(id));
    writeFieldDelta(w, d.changed[id as unknown as EntityId]!);
  }
  // `removed` holds the raw string map keys at runtime (Object.keys), so preserve them
  // as strings for exact round-trip fidelity (they are only ever used as record keys).
  w.varuint(d.removed.length);
  for (const id of d.removed) w.string(String(id));
  w.string(JSON.stringify(d.commandQueue));
}

function readDeltaBody(r: BinaryReader): WorldDeltaMessage {
  const rngState = r.number();
  const idCounter = r.number();
  const added = readEntities(r);
  const changedCount = r.varuint();
  const changed: Record<EntityId, EntityFieldDelta> = {};
  for (let i = 0; i < changedCount; i++) {
    const id = r.number() as EntityId;
    changed[id] = readFieldDelta(r);
  }
  const removedCount = r.varuint();
  const removed: EntityId[] = [];
  for (let i = 0; i < removedCount; i++) removed.push(r.string() as unknown as EntityId);
  const commandQueue = JSON.parse(r.string());
  return { kind: 'Delta', rngState, idCounter, added, changed, removed, commandQueue };
}

function writeKeyframeBody(w: BinaryWriter, k: WorldKeyframeMessage): void {
  w.number(k.rngState);
  w.number(k.idCounter);
  writeEntities(w, k.entities);
  w.string(JSON.stringify(k.commandQueue));
}

function readKeyframeBody(r: BinaryReader): WorldKeyframeMessage {
  const rngState = r.number();
  const idCounter = r.number();
  const entities = readEntities(r);
  const commandQueue = JSON.parse(r.string());
  return { kind: 'Keyframe', rngState, idCounter, entities, commandQueue };
}

/**
 * True iff `kind` is one of the three world messages this binary codec owns. The
 * {@link import('./protocol').encode} dispatch uses it to route only those kinds
 * through {@link encodeWorldMessage}; everything else stays JSON.
 */
export function isWorldMessageKind(kind: string): boolean {
  return kind === 'Snapshot' || kind === 'Delta' || kind === 'Keyframe';
}

/** Encode one world message into a framed binary payload (magic + version + kind + body). */
export function encodeWorldMessage(msg: WorldMessage): Uint8Array {
  const w = new BinaryWriter();
  w.u8(MAGIC);
  w.u8(FORMAT_VERSION);
  switch (msg.kind) {
    case 'Snapshot':
      w.u8(KIND_SNAPSHOT);
      writeSnapshotBody(w, msg);
      break;
    case 'Delta':
      w.u8(KIND_DELTA);
      writeDeltaBody(w, msg);
      break;
    case 'Keyframe':
      w.u8(KIND_KEYFRAME);
      writeKeyframeBody(w, msg);
      break;
  }
  return w.finish();
}

/**
 * Decode a framed binary world payload. Verifies the magic + format-version bytes and
 * **throws** on any mismatch or unknown kind - a corrupt/incompatible frame fails
 * loudly rather than yielding a half-built world (the client's receive guard turns the
 * throw into a dropped frame, never garbage).
 */
export function decodeWorldMessage(bytes: Uint8Array): WorldMessage {
  const r = new BinaryReader(bytes);
  const magic = r.u8();
  if (magic !== MAGIC) throw new Error(`bad binary magic 0x${magic.toString(16)}`);
  const version = r.u8();
  if (version !== FORMAT_VERSION) {
    throw new Error(`unsupported binary format version ${version} (expected ${FORMAT_VERSION})`);
  }
  const kind = r.u8();
  switch (kind) {
    case KIND_SNAPSHOT:
      return readSnapshotBody(r);
    case KIND_DELTA:
      return readDeltaBody(r);
    case KIND_KEYFRAME:
      return readKeyframeBody(r);
    default:
      throw new Error(`bad binary message kind ${kind}`);
  }
}
